import { useCallback, useContext, useEffect } from 'react';
import {
  differenceInYears,
  isDate,
  parseISO,
  format,
  differenceInWeeks,
} from 'date-fns';
import { FieldValues } from 'react-hook-form';
import { useDataLayerPush } from '@agria/theme/src/hooks/useDataLayerPush';
import { Customer, Pet, PetEquine } from '@agria/paws/src/types';
import { useBreed } from '@agria/paws/src/hooks/useBreed';
import { getValueFromId, isBrowser } from '@agria/utils';
import { useGender } from '@agria/paws/src/hooks/useGender';
import { useSexName } from '@agria/paws/src/hooks/useSexName';
import { useTitle } from '@agria/paws/src/hooks/useTitle';
import useContentTypeMapping from '@agria/theme/src/hooks/useContentTypeMapping';
import { useAffiliateDetails } from './useAffiliateDetails';
import { QuoteContext } from '../context/QuoteContext';
import { speciesReference } from '../const/speciesReference';
import { ActiveStepContext } from '../context/ActiveStepContext';
import { formStepDataLayerReference } from '../const/formStepDataLayerReference';
import { FormSteps } from '../types/global';
import useGetDataLayerContentType from './useGetDataLayerContentType';

interface DataLayerOptions {
  activeStepOverride?: FormSteps;
  event?: string;
  funnelStage?: string;
  funnelStageNumber?: number;
  interactionType?: string;
  contentType?: string;
  contentGroup?: string;
  agriaId?: string;
  pet?: Partial<Pet>;
  customer?: Partial<Customer>;
  quote?: Partial<BreederPolicyRequest>;
  selectedCoverOptionId?: string;
  selectedFixedExcessId?: string;
  selectedPreviousCustomer?: number;
  selectedExtras?: string[];
  howDidYouHear?: string;
  paymentFrequency?: string;
  transactionId?: string;
  policyValue?: number;
  errorMessage?: string;
  selectionType?: string;
  selectionNumber?: number;
  journeyNumber?: number;
  ownerType?: 'single' | 'multi';
  ownerNumber?: number;
  petNumber?: number;
  journey?: string;
  vetChecked?: boolean;
}

interface ErrorHandlingOptions {
  errorFields: FieldValues;
  funnelStage?: string;
  funnelStageNumber?: number;
  journey?: string;
}

interface useDataLayerPushReturn {
  activationDataLayerPush: (options: DataLayerOptions) => void;
  handleActivationDataLayerErrors: (options: ErrorHandlingOptions) => void;
}

const calculateUniqueOwners = (policyRequests, currentSubmission) => {
  const uniqueOwners = new Map();

  const addOwner = (owner, source = '') => {
    // Skip if required fields are missing
    if (!owner?.FirstName || !owner?.LastName) return;

    // Create a unique key that includes name and address details
    const key = [
      owner.FirstName.toLowerCase(),
      owner.LastName.toLowerCase(),
      owner.PostCode?.toLowerCase(),
      owner.AddressLine1?.toLowerCase(),
      owner.EmailAddress?.toLowerCase(),
      source, // Include source to differentiate additional policy holders
    ]
      .filter(Boolean)
      .join('-');

    uniqueOwners.set(key, {
      firstName: owner.FirstName,
      lastName: owner.LastName,
      postCode: owner.PostCode,
      source,
    });
  };

  // Process existing requests
  policyRequests?.forEach((request) => {
    const customer = request.Customer;
    if (!customer) return;

    // Skip if this is the same as current submission
    if (currentSubmission?.id === request.id) return;

    // Add main owner
    addOwner(customer, 'main');

    // Add additional policy holder if present
    if (customer.AdditionalFirstName && customer.AdditionalLastName) {
      addOwner(
        {
          FirstName: customer.AdditionalFirstName,
          LastName: customer.AdditionalLastName,
          PostCode: customer.PostCode, // Additional policy holder shares address
          AddressLine1: customer.AddressLine1,
        },
        'additional'
      );
    }
  });

  // Add current submission
  if (currentSubmission?.Customer) {
    const customer = currentSubmission.Customer;

    // Add main owner
    addOwner(customer, 'main');

    // Add additional policy holder if present
    if (customer.AdditionalFirstName && customer.AdditionalLastName) {
      addOwner(
        {
          FirstName: customer.AdditionalFirstName,
          LastName: customer.AdditionalLastName,
          PostCode: customer.PostCode, // Additional policy holder shares address
          AddressLine1: customer.AddressLine1,
        },
        'additional'
      );
    }
  }

  // Count unique owners
  const uniqueOwnerCount = uniqueOwners.size || 1;

  return {
    ownerType: uniqueOwnerCount > 1 ? 'multi' : 'single',
    ownerCount: uniqueOwnerCount,
  };
};

const formatActiveStep = (step: string) =>
  step
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const useActivationDataLayerPush = (): useDataLayerPushReturn => {
  const { dataLayerPush } = useDataLayerPush();
  const {
    activeQuote,
    activeSpecies,
    dataLayerContentType,
    petInfos,
    dispatch,
    totalQuoteRequests,
    additionalPolicyHolder,
    allActiveSpeciesCount,
    editMode,
    affiliatePolicyRequests,
    affiliateEnrolment,
    requestedQuotes,
  } = useContext(QuoteContext);
  const getDataLayerContentType = useGetDataLayerContentType();
  const { getBreeds } = useBreed(activeSpecies!);
  const { activeStep } = useContext(ActiveStepContext);
  const { affiliateType } = useAffiliateDetails();
  const sexes = useSexName();
  const genders = useGender();
  const titles = useTitle();
  const { getCurrentContentType } = useContentTypeMapping();

  const activationDataLayerPush = useCallback(
    (options: DataLayerOptions) => {
      const {
        activeStepOverride,
        event,
        interactionType,
        selectionType,
        selectionNumber,
        agriaId,
        pet,
        customer,
        funnelStageNumber,
        funnelStage,
        errorMessage,
        journeyNumber,
        ownerType: providedOwnerType,
        ownerNumber: providedOwnerNumber,
        selectedPreviousCustomer,
        journey,
        quote,
        vetChecked,
      } = options;

      if (!activeSpecies) {
        return null;
      }

      const currentJourneyNumber = parseInt(
        sessionStorage.getItem('journey_number') || '0',
        10
      );

      const cleanPostcode = (postcode?: string) => postcode?.split(' ')[0];

      const isEquine = (id: string) => speciesReference[id]?.name === 'Equine';

      const formatDate = (
        date: string | Date | undefined
      ): string | undefined => {
        if (!date) return undefined;

        if (typeof date === 'string') {
          try {
            const parsedDate = parseISO(date);
            if (isDate(parsedDate) && !Number.isNaN(parsedDate.getTime())) {
              return format(parsedDate, 'yyyy-MM-dd');
            }
          } catch (error) {
            console.error('Invalid date string provided:', date);
            return undefined;
          }
        } else if (date instanceof Date) {
          if (!Number.isNaN(date.getTime())) {
            return format(date, 'yyyy-MM-dd');
          }
        }

        return undefined;
      };

      const calculateAge = (
        dateOfBirth: Date | string | undefined,
        unit: 'years' | 'weeks' = 'years'
      ): { value: number | undefined; unit: 'years' | 'weeks' | undefined } => {
        if (!dateOfBirth) {
          return { value: undefined, unit: undefined };
        }

        let standardizedDate: Date;

        if (typeof dateOfBirth === 'string') {
          standardizedDate = parseISO(dateOfBirth);
        } else if (dateOfBirth instanceof Date) {
          standardizedDate = parseISO(dateOfBirth.toISOString());
        } else {
          console.error('Invalid date format:', dateOfBirth);
          return { value: undefined, unit: undefined };
        }

        if (
          !isDate(standardizedDate) ||
          Number.isNaN(standardizedDate.getTime())
        ) {
          console.error('Invalid date after standardization:', dateOfBirth);
          return { value: undefined, unit: undefined };
        }

        const currentDate = new Date();

        // Calculate the absolute differences
        const ageInWeeks = Math.abs(
          differenceInWeeks(currentDate, standardizedDate)
        );
        const ageInYears = Math.abs(
          differenceInYears(currentDate, standardizedDate)
        );

        // Always use weeks if the age is less than 1 year, regardless of requested unit
        if (ageInYears === 0) {
          return {
            value: ageInWeeks,
            unit: 'weeks',
          };
        }

        // If age is over 52 weeks or unit is specifically requested as years, return in years
        if (ageInWeeks > 52 || unit === 'years') {
          return {
            value: ageInYears,
            unit: 'years',
          };
        }

        // Otherwise return in weeks
        return {
          value: ageInWeeks,
          unit: 'weeks',
        };
      };

      // Helper function to remove undefined values and convert strings to lowercase recursively
      const cleanAndLowercase = (
        obj: Record<string, any>
      ): Record<string, any> =>
        Object.entries(obj).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            if (typeof value === 'string') {
              acc[key] = value.toLowerCase();
            } else if (Array.isArray(value)) {
              acc[key] = value.map((item) =>
                typeof item === 'string'
                  ? item.toLowerCase()
                  : typeof item === 'object' && item !== null
                  ? cleanAndLowercase(item)
                  : item
              );
            } else if (typeof value === 'object' && value !== null) {
              const cleanValue = cleanAndLowercase(value);
              // Preserve the object even if empty
              acc[key] = cleanValue;
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {} as Record<string, any>);

      const standardizeBreedName = (
        breedName: string,
        speciesId: string
      ): string => {
        if (!breedName) return breedName;

        const lowerBreed = breedName.toLowerCase();
        const isDog = speciesReference[speciesId]?.name.toLowerCase() === 'dog';
        const isCat = speciesReference[speciesId]?.name.toLowerCase() === 'cat';

        // Define all possible mixed breed terms
        const mixedBreedTerms = ['mixed breed', 'mongrel', 'crossbreed'];

        // Check if the breed name contains any of our mixed breed terms
        const isAnyMixedBreed = mixedBreedTerms.some(
          (term) =>
            lowerBreed.includes(term) ||
            lowerBreed.includes(term.replace(' ', '')) // Handle cases without space
        );

        if (isAnyMixedBreed) {
          // For cats, always return "mixed breed"
          if (isCat) {
            return 'mixed breed';
          }

          // For dogs, include size if present
          if (isDog) {
            const sizeMatch = lowerBreed.match(/\b(small|medium|large)\b/i);
            if (sizeMatch) {
              const size = sizeMatch[1].toLowerCase();
              return `mixed breed - ${size}`;
            }
            return 'mixed breed';
          }

          // For other species
          return 'mixed breed';
        }

        return breedName;
      };

      const breedName = (currIsEquine: boolean, currPet: Pet | PetEquine) => {
        // Use the current pet's species ID OR activeSpecies if the pet is missing it
        const speciesId = currPet?.SpeciesId || activeSpecies;

        if (!speciesId) {
          return undefined;
        }

        const breeds = getBreeds(speciesId);
        if (!breeds?.length) return undefined;

        // Check for LitterBreedId first (for litter capture journey), then fall back to regular BreedId
        const breedId = currIsEquine
          ? (currPet as PetEquine)?.HorseBreedId
          : currPet?.LitterBreedId || currPet?.BreedId;

        const rawBreedName = breedId
          ? getValueFromId(breedId, breeds)
          : undefined;

        return standardizeBreedName(rawBreedName || '', speciesId);
      };

      const fetchProductData = () => {
        // Create a mapping of all selected species
        const speciesMapping = {};

        // Handle requestedQuotes based on its structure
        if (requestedQuotes && typeof requestedQuotes === 'object') {
          // Convert the species IDs into an array
          const speciesIds = Object.keys(requestedQuotes);

          // Create a mapping for each species
          speciesIds.forEach((speciesId, index) => {
            const petNumber = index + 1;
            if (speciesReference[speciesId]) {
              speciesMapping[`pet_info_${petNumber}`] = {
                pet_species: speciesReference[speciesId].name.toLowerCase(),
              };
            }
          });
        }

        // Get the selected previous customer's data if it exists
        const existingCustomerData = selectedPreviousCustomer
          ? affiliatePolicyRequests?.find(
              (request) => request.id === selectedPreviousCustomer
            )?.Customer
          : null;

        // Check if we have a previous customer's data in the affiliatePolicyRequests
        const previousCustomerData = affiliatePolicyRequests?.find(
          (request) =>
            request.Customer?.FirstName && request.id < (options.petNumber || 0)
        )?.Customer;

        // Use the appropriate customer data source
        const customerData =
          options.customer ||
          customer ||
          activeQuote?.Customer ||
          existingCustomerData ||
          previousCustomerData;

        // Check if we have valid owner data in affiliatePolicyRequests
        const hasOwnerData = affiliatePolicyRequests?.some(
          (request) => request.Customer?.FirstName
        );

        const petNumber =
          editMode || options.petNumber || allActiveSpeciesCount;

        const ownerTitleValue = customerData?.TitleId
          ? getValueFromId(customerData.TitleId, titles)?.toLowerCase()
          : undefined;

        const postcodeOutcodeValue = customerData?.PostCode
          ? cleanPostcode(customerData.PostCode)?.toLowerCase()
          : undefined;

        // Get content mapping first - we need this for all cases
        const { contentGroup, contentType } = getCurrentContentType();

        // Get current pet and its species
        const currentPet = options.pet || pet || activeQuote?.Pet;
        const existingPetInfos = { ...petInfos };
        const existingPetInfo = existingPetInfos[`pet_info_${petNumber}`] || {};

        // Create or update pet infos based on species mapping
        Object.entries(speciesMapping).forEach(([petInfoKey, speciesInfo]) => {
          existingPetInfos[petInfoKey] = {
            ...(existingPetInfos[petInfoKey] || {}),
            ...speciesInfo,
          };
        });

        // If no species mapping but we know selection number, ensure correct number of pet infos
        if (
          Object.keys(speciesMapping).length === 0 &&
          options.selectionNumber
        ) {
          for (let i = 1; i <= options.selectionNumber; i++) {
            const petInfoKey = `pet_info_${i}`;
            if (!existingPetInfos[petInfoKey]) {
              existingPetInfos[petInfoKey] = {
                pet_species:
                  speciesReference[activeSpecies]?.name.toLowerCase(),
              };
            }
          }
        }

        // Get the correct species for the current pet
        const getCurrentSpecies = () => {
          // If we have affiliate policy requests, use those first
          if (affiliatePolicyRequests?.length > 0) {
            // Find the policy request matching our current pet number
            const matchingRequest = affiliatePolicyRequests.find(
              (request) => request.id === petNumber
            );

            if (matchingRequest?.Pet?.SpeciesId) {
              const species =
                speciesReference[matchingRequest.Pet.SpeciesId]?.name;
              return species;
            }
          }

          // If we're updating an existing pet info, maintain its species
          if (existingPetInfo.pet_species) {
            return existingPetInfo.pet_species;
          }

          // Use the species from requestedQuotes if available
          if (requestedQuotes?.length > 0) {
            const matchingQuote = requestedQuotes.find(
              (quote, index) => index + 1 === petNumber
            );
            if (matchingQuote?.speciesId) {
              const species = speciesReference[matchingQuote.speciesId]?.name;
              return species;
            }
          }

          // Fallback to current pet's species or active species
          const fallbackSpecies =
            speciesReference[currentPet?.SpeciesId || activeSpecies]?.name;
          return fallbackSpecies;
        };

        if (journey === 'breeder_enrolment') {
          Object.keys(existingPetInfos).forEach((key) => {
            if (existingPetInfos[key]) {
              delete existingPetInfos[key].owner_title;
              delete existingPetInfos[key].postcode_outcode;
            }
          });
        }

        // Get owner data for breeder_enrolment
        const breederOwnerData = {};
        if (journey === 'breeder_enrolment') {
          // Try customer from options first
          if (customer?.TitleId) {
            breederOwnerData.owner_title = getValueFromId(
              customer.TitleId,
              titles
            )?.toLowerCase();
          }
          if (customer?.PostCode) {
            breederOwnerData.postcode_outcode = cleanPostcode(
              customer.PostCode
            )?.toLowerCase();
          }

          // Check quote/options first for investigation and breeding status
          if (quote?.SubjectToInvestigation !== undefined) {
            breederOwnerData.breeder_investigation =
              quote.SubjectToInvestigation ? 'yes' : 'no';
          }
          if (quote?.SelfBreedAllLittersSold !== undefined) {
            breederOwnerData.breed_themselves = quote.SelfBreedAllLittersSold
              ? 'yes'
              : 'no';
          }

          // If not in quote/options, try affiliateEnrolment
          if (!breederOwnerData.owner_title && affiliateEnrolment?.TitleId) {
            breederOwnerData.owner_title = getValueFromId(
              affiliateEnrolment.TitleId,
              titles
            )?.toLowerCase();
          }
          if (
            !breederOwnerData.postcode_outcode &&
            affiliateEnrolment?.PostCode
          ) {
            breederOwnerData.postcode_outcode = cleanPostcode(
              affiliateEnrolment.PostCode
            )?.toLowerCase();
          }
          if (
            breederOwnerData.breeder_investigation === undefined &&
            affiliateEnrolment?.SubjectToInvestigation !== undefined
          ) {
            breederOwnerData.breeder_investigation =
              affiliateEnrolment.SubjectToInvestigation ? 'yes' : 'no';
          }
          if (
            breederOwnerData.breed_themselves === undefined &&
            affiliateEnrolment?.SelfBreedAllLittersSold !== undefined
          ) {
            breederOwnerData.breed_themselves =
              affiliateEnrolment.SelfBreedAllLittersSold ? 'yes' : 'no';
          }
        }

        // Get the correct species ID based on existing or current data
        const existingSpecies = existingPetInfo.pet_species;
        const speciesId = existingSpecies
          ? Object.entries(speciesReference).find(
              ([_, value]) =>
                value.name.toLowerCase() === existingSpecies.toLowerCase()
            )?.[0]
          : currentPet?.SpeciesId || activeSpecies;

        const currentSpecies = getCurrentSpecies();
        const breeds = getBreeds(speciesId);

        const baseData = {
          event: event || 'activation_journey_interaction',
          content_group: contentGroup,
          content_type: contentType,
          funnel_stage:
            funnelStage || formatActiveStep(activeStepOverride || activeStep),
          funnel_stage_number:
            funnelStageNumber ||
            formStepDataLayerReference[activeStep]?.funnelStageNumber,
          interaction_type: interactionType,
          selection_type:
            selectionType ||
            getDataLayerContentType({
              requestedQuotes,
              journey,
            }),
          journey_number: journeyNumber || currentJourneyNumber,
          customer_type: `${affiliateType || 'owner'}`,
          agria_id: agriaId !== undefined ? agriaId : undefined,

          // Include owner parameters if we have owner data in affiliatePolicyRequests
          ...((hasOwnerData || customer || activeQuote?.Customer) &&
            journey !== 'litter_capture' &&
            journey !== 'breeder_enrolment' && {
              ...(() => {
                // Create full current submission object
                const currentSubmission = {
                  id: quote?.id,
                  Customer: {
                    ...customer,
                    ...activeQuote?.Customer,
                    // Include any additional policyholder data
                    ...(additionalPolicyHolder && {
                      AdditionalFirstName: additionalPolicyHolder?.FirstName,
                      AdditionalLastName: additionalPolicyHolder?.LastName,
                    }),
                  },
                };

                const { ownerType, ownerCount } = calculateUniqueOwners(
                  affiliatePolicyRequests,
                  currentSubmission
                );

                return {
                  owner_type: providedOwnerType || ownerType,
                  owner_number: providedOwnerNumber || ownerCount,
                };
              })(),
            }),

          // Only include these if not litter capture journey
          ...(journey !== 'litter_capture' && {
            ...(() => ({
              selection_number: selectionNumber || totalQuoteRequests,
            }))(),
          }),
          // Only include these if breeder enrolment
          ...(journey === 'breeder_enrolment' ? breederOwnerData : {}),
          error_message: errorMessage !== undefined ? errorMessage : undefined,
        };

        // Check for a meaningful pet object (more than just SpeciesId)
        const hasMeaningfulPet =
          currentPet &&
          Object.keys(currentPet).length > 1 &&
          Object.keys(currentPet).some((key) => key !== 'SpeciesId');

        // Don't create new pet info if we're not updating a pet
        if (existingPetInfos[`pet_info_${petNumber}`] || hasMeaningfulPet) {
          const updatedPetInfo = {
            ...existingPetInfo,
            // Always maintain existing species and breed unless we have meaningful new data
            pet_species: currentSpecies?.toLowerCase(),

            pet_breed:
              existingPetInfo.pet_breed ||
              (hasMeaningfulPet
                ? breedName(
                    isEquine(currentPet?.SpeciesId || activeSpecies),
                    currentPet
                  )
                : undefined),

            first_litter: (() => {
              if (currentPet?.FirstLitter === undefined) {
                return undefined;
              }
              if (currentPet.FirstLitter === true) {
                return 'yes';
              }
              return 'no';
            })(),
            litter_date: currentPet?.LitterReadyDate
              ? formatDate(currentPet.LitterReadyDate)
              : undefined,
            litter_now:
              currentPet?.LitterReady !== undefined
                ? currentPet.LitterReady
                  ? 'yes'
                  : 'no'
                : undefined,
            ...(journey === 'breeder_enrolment' && {
              pet_gender: 'female',
            }),
            ...(journey === 'rehoming' && {
              vet_health_check:
                currentPet?.VetChecked !== undefined
                  ? currentPet?.VetChecked
                    ? 'yes'
                    : 'no'
                  : undefined,
            }),
          };
          // Only add owner data if NOT breeder_enrolment
          if (
            journey !== 'breeder_enrolment' &&
            (customerData || previousCustomerData)
          ) {
            Object.assign(updatedPetInfo, {
              owner_title: ownerTitleValue || existingPetInfo.owner_title,
              postcode_outcode:
                postcodeOutcodeValue || existingPetInfo.postcode_outcode,
            });
          }

          // Handle breed based on the correct species
          if (hasMeaningfulPet) {
            updatedPetInfo.pet_breed = breedName(isEquine(speciesId), {
              ...currentPet,
              SpeciesId: speciesId,
            });
          } else if (currentPet?.BreedId && breeds) {
            updatedPetInfo.pet_breed = getValueFromId(
              currentPet.BreedId,
              breeds
            )?.toLowerCase();
          } else {
            updatedPetInfo.pet_breed = existingPetInfo.pet_breed;
          }

          const ageResult = calculateAge(currentPet?.DateOfBirth);

          // Only add/update other pet-specific data if we have meaningful pet data
          if (hasMeaningfulPet) {
            Object.assign(updatedPetInfo, {
              pet_age: ageResult.value,
              pet_age_unit: ageResult.unit,
              ...(journey !== 'breeder_enrolment' && {
                pet_gender: getValueFromId(
                  currentPet.GenderId,
                  genders
                )?.toLowerCase(),
              }),
              pet_purchase_price:
                currentPet?.Cost === 0 ? undefined : currentPet?.Cost,
              pet_collection_date: formatDate(
                currentPet?.PolicyStartDate || activeQuote?.PolicyStartDate
              ),
            });
          }

          // Owner data should be added regardless of whether we have meaningful pet data
          if (customerData || previousCustomerData) {
            if (journey !== 'breeder_enrolment') {
              Object.assign(updatedPetInfo, {
                owner_title: ownerTitleValue || existingPetInfo.owner_title,
                postcode_outcode:
                  postcodeOutcodeValue || existingPetInfo.postcode_outcode,
              });
            }
          }

          existingPetInfos[`pet_info_${petNumber}`] = updatedPetInfo;

          dispatch({
            type: 'UPDATE_ALL_PET_INFOS',
            payload: existingPetInfos,
          });
        }

        // Create final data object
        const finalData = {
          ...baseData,
          ...(activeQuote?.Pet?.VetChecked && {
            vet_vaccination_date: formatDate(activeQuote.Pet.VetChecked),
          }),
          ...existingPetInfos,
        };

        const cleanedData = cleanAndLowercase(finalData);
        dataLayerPush(cleanedData);
      };

      fetchProductData();
    },
    [
      activeQuote,
      activeSpecies,
      activeStep,
      dataLayerPush,
      genders,
      titles,
      affiliateType,
      totalQuoteRequests,
      petInfos,
      dispatch,
      allActiveSpeciesCount,
      editMode,
      getBreeds,
      affiliatePolicyRequests,
      getDataLayerContentType,
      affiliateEnrolment,
      requestedQuotes,
      getCurrentContentType,
      additionalPolicyHolder,
    ]
  );

  const handleActivationDataLayerErrors = useCallback(
    ({
      errorFields,
      funnelStage,
      funnelStageNumber,
      journey,
    }: ErrorHandlingOptions) => {
      Object.keys(errorFields).forEach((key) => {
        const err = errorFields[key];
        activationDataLayerPush({
          errorMessage: err.message === '' ? 'required' : err.message,
          event: journey
            ? `${journey}_journey_interaction`
            : 'activation_journey_interaction',
          funnelStage: funnelStage ?? formatActiveStep(activeStep),
          funnelStageNumber:
            funnelStageNumber ||
            formStepDataLayerReference[activeStep]?.funnelStageNumber,
          interactionType: `error on: ${key}`,
        });
      });
    },
    [activationDataLayerPush, activeStep]
  );

  return {
    handleActivationDataLayerErrors,
    activationDataLayerPush,
  };
};
